import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Shield, Award, Zap, Mail, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import HowItWorks from '../components/HowItWorks';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// New Gold & Black Logo
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_7d9018ae-dc6c-4832-b25d-d9d05e8d5132/artifacts/j9dbuwqr_Vector%20File.jpg';

const HERO_IMAGE = 'https://customer-assets.emergentagent.com/job_80049ad6-9b9f-41a5-afd5-81092faf4bc8/artifacts/oqhszyxl_Benda%20Hero%20image.png';

const features = [
  { icon: Shield, title: 'Premium Protection', desc: 'CE certified armor & materials' },
  { icon: Award, title: 'Top Brands', desc: 'Fulmer, LS2, & equivalent' },
  { icon: Zap, title: 'Quick Delivery', desc: 'Direct to hotel or Airbnb' }
];

// Product Card for Homepage
const HomeProductCard = ({ product }) => {
  const isOutOfStock = product.is_coming_soon || product.inventory_quantity === 0;

  return (
    <div className="group relative bg-white border border-gray-200 hover:border-[#C6A87C]/50 overflow-hidden product-card">
      <Link to={`/products/${product.id}`}>
        <div className="aspect-[4/3] overflow-hidden bg-white relative">
          <img
            src={product.image_url}
            alt={product.title}
            className="w-full h-full object-contain p-4 transition-transform duration-500 group-hover:scale-105"
          />
          {isOutOfStock && (
            <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
              <span className="bg-[#1a1a1a] text-white font-bold uppercase tracking-wider px-4 py-2 text-sm">
                Coming Soon
              </span>
            </div>
          )}
        </div>
      </Link>
      <div className="p-6 border-t border-gray-100">
        <p className="text-xs text-gray-500 uppercase tracking-widest mb-2">
          {product.vendor || 'Essential Series'}
        </p>
        <Link to={`/products/${product.id}`}>
          <h3 className="font-['Barlow_Condensed'] text-xl font-semibold uppercase tracking-tight mb-2 text-gray-900 hover:text-[#C6A87C] transition-colors">
            {product.title}
          </h3>
        </Link>
        <div className="flex items-center justify-between">
          <div>
            <span className="text-2xl font-bold text-gray-900">${product.price}</span>
            <span className="text-sm text-gray-500 ml-1">/day</span>
          </div>
          <Link to={`/products/${product.id}`}>
            <Button
              className="bg-[#1a1a1a] text-white hover:bg-[#333] font-bold uppercase tracking-wider text-sm px-4"
            >
              {isOutOfStock ? 'View' : 'Rent Now'}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

const HomePage = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products`);
        // Get first 3 products
        setFeaturedProducts(response.data.slice(0, 3));
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative w-full" style={{ paddingTop: '20px' }} data-testid="hero-section">
        {/* Hero Image - Full Width */}
        <div className="relative w-full">
          <img 
            src={HERO_IMAGE} 
            alt="Gear Up - Ride Out - Motorcycle Gear Rentals" 
            className="w-full h-auto object-contain"
          />
          {/* Clickable overlay on the RENT YOUR GEAR button area */}
          <Link 
            to="/products" 
            className="absolute cursor-pointer"
            style={{
              bottom: '4%',
              left: '3%',
              width: '22%',
              height: '8%',
            }}
            data-testid="hero-rent-btn"
          >
            <span className="sr-only">Rent Your Gear</span>
          </Link>
        </div>
      </section>

      {/* Features Bar */}
      <section className="bg-gray-50 border-y border-gray-200 py-8" data-testid="features-bar">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#C6A87C]/10 flex items-center justify-center">
                  <feature.icon className="w-6 h-6 text-[#C6A87C]" strokeWidth={1.5} />
                </div>
                <div>
                  <h4 className="font-['Barlow_Condensed'] font-semibold uppercase tracking-wide text-gray-900">
                    {feature.title}
                  </h4>
                  <p className="text-sm text-gray-500">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <HowItWorks />

      {/* Featured Products */}
      <section className="py-20 md:py-32 px-6 md:px-12 bg-white" data-testid="featured-products">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12">
            <div>
              <p className="text-sm text-[#C6A87C] uppercase tracking-widest mb-4">Featured</p>
              <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold uppercase tracking-tight text-gray-900">
                Popular Gear
              </h2>
            </div>
            <Link to="/products" className="mt-4 md:mt-0">
              <Button 
                variant="outline" 
                className="border-gray-300 text-gray-700 hover:border-[#1a1a1a] hover:bg-gray-50 uppercase tracking-wider"
                data-testid="view-all-btn"
              >
                View All
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-white border border-gray-200 animate-pulse">
                  <div className="aspect-[4/3] bg-gray-100"></div>
                  <div className="p-6 space-y-3">
                    <div className="h-4 bg-gray-100 w-1/4"></div>
                    <div className="h-6 bg-gray-100 w-3/4"></div>
                    <div className="h-4 bg-gray-100 w-full"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredProducts.map(product => (
                <HomeProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Trust & Security Banner */}
      <section className="py-12 px-6 md:px-12 bg-gray-50 border-y border-gray-200" data-testid="trust-banner">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#C6A87C]/10 flex items-center justify-center flex-shrink-0">
                <Shield className="w-6 h-6 text-[#C6A87C]" />
              </div>
              <div>
                <h4 className="font-['Barlow_Condensed'] font-semibold uppercase tracking-wide mb-1 text-gray-900">
                  ID Verification
                </h4>
                <p className="text-sm text-gray-500">
                  Quick 2-minute verification ensures community safety and gear quality.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#C6A87C]/10 flex items-center justify-center flex-shrink-0">
                <Award className="w-6 h-6 text-[#C6A87C]" />
              </div>
              <div>
                <h4 className="font-['Barlow_Condensed'] font-semibold uppercase tracking-wide mb-1 text-gray-900">
                  Security Deposit
                </h4>
                <p className="text-sm text-gray-500">
                  Refundable hold released within 3-5 days of successful return.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#C6A87C]/10 flex items-center justify-center flex-shrink-0">
                <Zap className="w-6 h-6 text-[#C6A87C]" />
              </div>
              <div>
                <h4 className="font-['Barlow_Condensed'] font-semibold uppercase tracking-wide mb-1 text-gray-900">
                  Hotel & Airbnb Delivery
                </h4>
                <p className="text-sm text-gray-500">
                  Ship gear directly to your hotel concierge or vacation rental.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-12 px-6 md:px-12" data-testid="footer">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            {/* Logo & Description */}
            <div className="md:col-span-1">
              <Link to="/" className="flex flex-col items-start gap-0.5 mb-4">
                <div style={{ height: '50px', overflow: 'hidden' }}>
                  <img 
                    src={LOGO_URL} 
                    alt="My Gear Rental" 
                    style={{ height: '100px', objectFit: 'contain', objectPosition: 'center top' }}
                  />
                </div>
                <span style={{ fontSize: '10px', color: '#333333', letterSpacing: '0.5px' }}>
                  Premium Gear. Trusted Journeys.
                </span>
              </Link>
              <p className="text-sm text-gray-500">
                Premium motorcycle gear rentals. Access world-class protection without the commitment.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-['Barlow_Condensed'] font-semibold uppercase tracking-wider mb-4 text-gray-900">Quick Links</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/products" className="text-gray-500 hover:text-[#C6A87C] transition-colors">All Gear</Link></li>
                <li><Link to="/how-it-works" className="text-gray-500 hover:text-[#C6A87C] transition-colors">How It Works</Link></li>
                <li><Link to="/faq" className="text-gray-500 hover:text-[#C6A87C] transition-colors">FAQ</Link></li>
              </ul>
            </div>

            {/* Support */}
            <div>
              <h4 className="font-['Barlow_Condensed'] font-semibold uppercase tracking-wider mb-4 text-gray-900">Support</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/faq" className="text-gray-500 hover:text-[#C6A87C] transition-colors">FAQ</Link></li>
                <li><Link to="/terms" className="text-gray-500 hover:text-[#C6A87C] transition-colors">Terms of Service</Link></li>
                <li><Link to="/terms-and-conditions" className="text-gray-500 hover:text-[#C6A87C] transition-colors">Terms and Conditions</Link></li>
                <li><a href="mailto:support@mygearrental.com" className="text-gray-500 hover:text-[#C6A87C] transition-colors">Contact Us</a></li>
              </ul>
            </div>

            {/* Newsletter Signup */}
            <div>
              <h4 className="font-['Barlow_Condensed'] font-semibold uppercase tracking-wider mb-4 text-gray-900">Newsletter & Promos</h4>
              <p className="text-sm text-gray-500 mb-3">
                Get exclusive deals, new gear alerts, and riding tips.
              </p>
              <form 
                className="flex flex-col gap-2" 
                onSubmit={(e) => {
                  e.preventDefault();
                  const email = e.target.email.value;
                  if (email) {
                    toast.success('Thanks for subscribing!');
                    e.target.reset();
                  }
                }}
                data-testid="newsletter-form"
              >
                <input 
                  type="email" 
                  name="email"
                  placeholder="Enter your email"
                  className="w-full h-10 px-3 bg-gray-50 border border-gray-200 text-sm placeholder:text-gray-400 focus:border-[#1a1a1a] focus:outline-none transition-colors text-gray-900"
                  data-testid="newsletter-email"
                />
                <button 
                  type="submit"
                  className="w-full h-10 bg-[#1a1a1a] text-white font-bold uppercase tracking-wider text-xs hover:bg-[#333] transition-colors"
                  data-testid="newsletter-submit"
                >
                  Subscribe
                </button>
              </form>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-500">
              © 2024 My Gear Rental. Premium motorcycle gear rentals.
            </p>
            <p className="text-xs text-gray-500">
              Premium Gear. Trusted Journeys.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
